import React, { useState, useRef, useEffect } from 'react'
import { toast } from 'react-toastify'
import TypeSelector from './TypeSelector'

const REGIONS_DATA = {
	NORTHEAST: ['CT', 'DE', 'ME', 'MD', 'MA', 'NH', 'NJ', 'NY', 'PA', 'RI', 'VT'],
	MIDWEST: ['IL', 'IN', 'IA', 'KS', 'MI', 'MN', 'MO', 'NE', 'ND', 'OH', 'SD', 'WI'],
	SOUTHEAST: ['AL', 'AR', 'FL', 'GA', 'KY', 'LA', 'MS', 'NC', 'SC', 'TN', 'VA', 'WV'],
	SOUTHWEST: ['AZ', 'CA', 'CO', 'HI', 'NV', 'NM', 'OK', 'TX', 'UT'],
	NORTHWEST: ['AK', 'ID', 'MT', 'OR', 'WA', 'WY'],
}

export default function RegionDestinationFilter({
	filters,
	setFilters,
	labels,
	styles,
	isDarkMode,
	destinationType,
	setDestinationType,
}) {
	const [selectedRegions, setSelectedRegions] = useState(filters.destinationRegions || [])
	const [showRegionDropdown, setShowRegionDropdown] = useState(false)
	const regionDropdownRef = useRef(null)

	// Sync with external filter changes (e.g., when X button is clicked or Clear Filters)
	useEffect(() => {
		setSelectedRegions(filters.destinationRegions || [])
	}, [filters.destinationRegions])

	// Close region dropdown when clicking outside
	useEffect(() => {
		const handleDocumentClick = (event) => {
			if (regionDropdownRef.current && !regionDropdownRef.current.contains(event.target)) {
				setShowRegionDropdown(false)
			}
		}

		document.addEventListener('mousedown', handleDocumentClick)
		return () => {
			document.removeEventListener('mousedown', handleDocumentClick)
		}
	}, [])

	const handleRegionToggle = (region) => {
		const isSelected = selectedRegions.includes(region)
		const newRegions = isSelected
			? selectedRegions.filter(r => r !== region)
			: [...selectedRegions, region]
		
		// Check limit when adding new region
		if (!isSelected && newRegions.length > 3) {
			toast.warning('Maximum 3 regions allowed')
			return
		}
		
		setSelectedRegions(newRegions)
		setFilters(prev => ({ 
			...prev, 
			destinationRegions: newRegions,
			paginateSize: 1
		}))
	}


	return (
		<div className="join flex flex-1" ref={regionDropdownRef}>
			<TypeSelector
				destinationType={destinationType}
				setDestinationType={setDestinationType}
				labels={labels}
				styles={styles}
				isDarkMode={isDarkMode}
			/>

			{/* Region Selector */}
			<div className="input-container flex-grow relative">
				<input
					type="text"
					id="region-input"
					className="join-item mt-1 p-2 w-full focus:ring-0 focus:outline-none"
					style={{ 
						...styles, 
						height: '40px',
						boxSizing: 'border-box',
						fontSize: '14px',
						borderTopLeftRadius: '0',
						borderBottomLeftRadius: '0',
						borderTopRightRadius: '6px',
						borderBottomRightRadius: '6px',
						outline: 'none',
						border: `1px solid ${isDarkMode ? '#404040' : '#cccccc'}`,
					}}
					value={selectedRegions
						.map(region => region.charAt(0).toUpperCase() + region.slice(1).toLowerCase())
						.join(', ')}
					onFocus={() => {
						setShowRegionDropdown(true)
					}}
					onClick={() => {
						setShowRegionDropdown(true)
					}}
					onChange={() => {}}
					autoComplete="off"
				/>
				<label
					htmlFor="region-input"
					className={
						selectedRegions.length > 0
							? 'filled'
							: ''
					}
					style={{ ...labels, backgroundColor: isDarkMode ? '#1c2126' : '#ffffff',
							color: isDarkMode ? '#C9D1D9' : '#3a4353' }}
					onClick={(e) => {
						const container = e.target.closest('.input-container')
						if (container) {
							const inputElement = container.querySelector('input')
							if (inputElement) {
								inputElement.focus()
							}
						}
					}}
				>
					Regions{' '}
					<span style={{ fontSize: '0.99em', opacity: 0.7 }}>(up to 3)</span>
				</label>
				{showRegionDropdown && (
					<div
						className="absolute left-0 right-0 bg-white border shadow-lg z-50"
						style={{
							top: '52px',
							backgroundColor: isDarkMode ? '#1a1a1a' : 'white',
							borderColor: isDarkMode ? '#404040' : '#cccccc',
							borderRadius: '12px',
							boxShadow: isDarkMode
								? '0 12px 28px rgba(0, 0, 0, 0.32), 0 2px 8px rgba(0, 0, 0, 0.2)'
								: '0 10px 24px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06)',
							maxHeight: '200px',
							overflowY: 'auto',
						}}
					>
						{['NORTHEAST', 'MIDWEST', 'SOUTHEAST', 'SOUTHWEST', 'NORTHWEST'].map(region => (
							<div
								key={region}
								onClick={(e) => {
									handleRegionToggle(region)
									// Clear any hover background that might be stuck
									e.currentTarget.style.backgroundColor = selectedRegions.includes(region) ? '#3b82f6' : 'transparent'
								}}
								className="px-2 py-1 cursor-pointer flex items-center justify-between text-sm"
								style={{
									backgroundColor: selectedRegions.includes(region)
										? '#3b82f6'
										: 'transparent',
									color: selectedRegions.includes(region)
										? 'white'
										: isDarkMode ? 'white' : '#000000',
									borderRadius: 0,
								}}
								onMouseEnter={(e) => {
									if (!selectedRegions.includes(region)) {
										e.currentTarget.style.backgroundColor = isDarkMode ? '#374151' : '#f3f4f6'
									}
								}}
								onMouseLeave={(e) => {
									if (!selectedRegions.includes(region)) {
										e.currentTarget.style.backgroundColor = 'transparent'
									}
								}}
							>
								<span>
									<span className="font-medium">{region.charAt(0).toUpperCase() + region.slice(1).toLowerCase()}</span>
									<span className="text-xs opacity-30 ml-2">
										({REGIONS_DATA[region]?.join(', ')})
									</span>
								</span>
								{selectedRegions.includes(region) && (
									<svg className="h-4 w-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
									</svg>
								)}
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	)
}
